/**
 * Os diálogos do Desk Cloud (docs/spec/desk-cloud.md): "Quem pode abrir" (as mesas de cada arquivo) e
 * "Mover para" (a árvore de pastas).
 */
import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';
import { folderSubtree, type DeskFile, type DeskFolder } from '../shared.js';
import { Button, Dialog } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { toast } from '../ui/Toaster.js';
import { newFolder, setRooms } from './cloudActions.js';
import { childFolders, roomColor, roomInitials } from './cloudModel.js';
import { useCloud } from './cloudStore.js';

/** Uma caixa com o terceiro estado (alguns arquivos ligados, outros não). */
function TriCheck({ state, onChange, labelledBy }: { state: boolean | 'mixed'; onChange: () => void; labelledBy: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'mixed';
  }, [state]);
  return <input ref={ref} type="checkbox" checked={state === true} aria-checked={state === 'mixed' ? 'mixed' : state} aria-labelledby={labelledBy} onChange={onChange} />;
}

/**
 * Quem pode abrir: as mesas em que a pessoa mestra, cada uma com uma caixa (de três estados, com vários
 * arquivos). As mesas em que ela joga aparecem só para desligar. Só o que mudou é gravado.
 */
export function ShareDialog({ files, onClose }: { files: DeskFile[]; onClose: () => void }) {
  const t = useT();
  const rooms = useCloud((s) => s.summary?.rooms) ?? [];
  const initial = (roomId: string): boolean | 'mixed' => {
    const n = files.filter((file) => file.rooms.includes(roomId)).length;
    return n === 0 ? false : n === files.length ? true : 'mixed';
  };
  const gmRooms = rooms.filter((room) => room.gm);
  const playerRooms = rooms.filter((room) => !room.gm && files.some((file) => file.rooms.includes(room.id)));
  const [state, setState] = useState<Record<string, boolean | 'mixed'>>(() => Object.fromEntries([...gmRooms, ...playerRooms].map((room) => [room.id, initial(room.id)])));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const add = gmRooms.filter((room) => state[room.id] === true && initial(room.id) !== true).map((room) => room.id);
    const remove = [...gmRooms, ...playerRooms].filter((room) => state[room.id] === false && initial(room.id) !== false).map((room) => room.id);
    setBusy(true);
    const ok = await setRooms(files, add, remove);
    setBusy(false);
    if (!ok) return;
    if (add.length || remove.length) toast(t('cloud.share.saved'), 'success');
    onClose();
  };

  // na mesa que a pessoa mestra, liga e desliga; na que ela joga, só desliga (ou volta a como estava)
  const toggle = (roomId: string, gm: boolean) =>
    setState((current) => {
      const now = current[roomId] ?? false;
      if (gm) return { ...current, [roomId]: now !== true };
      return { ...current, [roomId]: now === false ? initial(roomId) : false };
    });

  const row = (room: (typeof rooms)[number], gm: boolean) => {
    const value = state[room.id] ?? false;
    const id = `rd-share-${room.id}`;
    return (
      <label key={room.id} className="rd-cloudShare__room">
        <TriCheck state={value} labelledBy={id} onChange={() => toggle(room.id, gm)} />
        <span className="rd-cloudNav__room" style={{ background: roomColor(room.id) }} aria-hidden="true">
          {roomInitials(room.name)}
        </span>
        <span className="rd-cloudShare__text" id={id}>
          <span>{room.name}</span>
          <span className="rd-cloudShare__hint">{gm ? t('cloud.share.gmHint', { n: room.members }) : t('cloud.share.playerHint')}</span>
        </span>
      </label>
    );
  };

  return (
    <Dialog
      title={t('cloud.share.title')}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" busy={busy} onClick={() => void save()}>
            {t('cloud.details.save')}
          </Button>
        </>
      }
    >
      <p className="rd-cloudShare__target">{files.length === 1 ? files[0]!.name : t('cloud.share.files', { n: files.length })}</p>
      <div className="rd-cloudShare__owner">
        <span className="rd-cloudPeople__avatar" aria-hidden="true">
          <Icon name="user" size={14} />
        </span>
        <span className="rd-cloudShare__text">
          <span>{t('cloud.share.you')}</span>
          <span className="rd-cloudShare__hint">{t('cloud.share.youHint')}</span>
        </span>
      </div>
      <div className="rd-cloudShare__group">{t('cloud.share.gmRooms')}</div>
      {gmRooms.length ? gmRooms.map((room) => row(room, true)) : <p className="rd-cloudShare__hint">{t('cloud.share.noRooms')}</p>}
      {playerRooms.length ? (
        <>
          <div className="rd-cloudShare__group">{t('cloud.share.playerRooms')}</div>
          {playerRooms.map((room) => row(room, false))}
        </>
      ) : null}
      <p className="rd-cloudShare__note">
        <Icon name="link" size={15} />
        <span>{t('cloud.share.note')}</span>
      </p>
    </Dialog>
  );
}

/**
 * Mover para: a árvore de pastas, com a raiz e "Nova pasta aqui". Ao mover uma pasta, ela e as de
 * dentro dela não são destino.
 */
export function FolderPickerDialog({
  folders,
  moving,
  title,
  onPick,
  onClose,
}: {
  folders: readonly DeskFolder[];
  moving: DeskFolder | null;
  title: string;
  onPick: (folderId: string | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [chosen, setChosen] = useState<string | null>(moving?.parentId ?? null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const blocked = moving ? folderSubtree(folders, moving.id) : new Set<string>();

  const tree = (parentId: string | null, depth: number): React.ReactNode =>
    childFolders(folders, parentId)
      .filter((folder) => !blocked.has(folder.id))
      .map((folder) => {
        const kids = folders.some((f) => f.parentId === folder.id && !blocked.has(f.id));
        const expanded = open.has(folder.id);
        return (
          <div key={folder.id} role="group">
            <div className="rd-cloudPicker__row" style={{ '--depth': depth } as React.CSSProperties}>
              {kids ? (
                <button
                  type="button"
                  className={`rd-cloudNav__toggle${expanded ? ' is-open' : ''}`}
                  aria-label={t('cloud.folder.inside', { name: folder.name })}
                  aria-expanded={expanded}
                  onClick={() => setOpen((current) => new Set(current.has(folder.id) ? [...current].filter((id) => id !== folder.id) : [...current, folder.id]))}
                >
                  <Icon name="chevronRight" size={14} />
                </button>
              ) : (
                <span className="rd-cloudNav__toggle" aria-hidden="true" />
              )}
              <button type="button" className={`rd-cloudPicker__item${chosen === folder.id ? ' is-on' : ''}`} aria-pressed={chosen === folder.id} onClick={() => setChosen(folder.id)}>
                <Icon name="folder" size={16} />
                {folder.name}
                <span className="rd-cloudNav__count">{folder.files}</span>
              </button>
            </div>
            {kids && expanded ? tree(folder.id, depth + 1) : null}
          </div>
        );
      });

  return (
    <Dialog
      title={title}
      onClose={onClose}
      actions={
        <>
          <Button
            variant="secondary"
            icon="folderPlus"
            onClick={async () => {
              const created = await newFolder(chosen);
              if (created) {
                setChosen(created);
                if (chosen) setOpen((current) => new Set([...current, chosen]));
              }
            }}
          >
            {t('cloud.menu.newFolderHere')}
          </Button>
          <span className="rd-cloudTools__gap" />
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" onClick={() => onPick(chosen)}>
            {t('cloud.folder.move')}
          </Button>
        </>
      }
    >
      <div className="rd-cloudPicker" role="tree" aria-label={title}>
        <div className="rd-cloudPicker__row">
          <span className="rd-cloudNav__toggle" aria-hidden="true" />
          <button type="button" className={`rd-cloudPicker__item${chosen === null ? ' is-on' : ''}`} aria-pressed={chosen === null} onClick={() => setChosen(null)}>
            <Icon name="cloud" size={16} />
            {t('cloud.title')}
          </button>
        </div>
        {tree(null, 1)}
      </div>
    </Dialog>
  );
}
